import { useState, useEffect, useRef } from "react";
import { Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScrollContainer } from "@/hooks/useScrollContainer";

export default function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  const isMobile = useIsMobile();
  const scrollRef = useScrollContainer();
  const handlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    let frame = 0;

    const bind = () => {
      const el = scrollRef.current;
      if (handlerRef.current && el) {
        el.removeEventListener("scroll", handlerRef.current);
        handlerRef.current = null;
      }
      if (!el) {
        if (!cancelled && frame < 10) { frame++; requestAnimationFrame(bind); }
        return;
      }
      const handler = () => setVisible(el.scrollTop > 20);
      handlerRef.current = handler;
      handler();
      el.addEventListener("scroll", handler, { passive: true });
    };

    bind();

    return () => {
      cancelled = true;
      const el = scrollRef.current;
      if (handlerRef.current && el) el.removeEventListener("scroll", handlerRef.current);
      handlerRef.current = null;
    };
  }, [scrollRef]);

  const handleClick = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!visible || isMobile) return null;

  return (
    <button
      onClick={handleClick}
      className={cn(
        "fixed bottom-36 right-[18px] z-50",
        "flex items-center justify-center rounded-full",
        "h-8 w-8",
        "bg-primary/20 hover:bg-primary/30 border border-primary/40",
        "text-primary transition-all duration-300",
        "animate-fade-in"
      )}
      aria-label="Remonter en haut"
      title="Remonter en haut"
    >
      <Zap className="h-4 w-4 rotate-180" />
    </button>
  );
}
